import moment from "moment";
import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import { AbsenceLiteSalarieV2Dto } from "../../../dto/GenApi.iNotPaie.Dto/AbsenceLiteSalarieV2Dto";
import { AbsenceSalarieDto } from "../../../dto/GenApi.iNotPaie.Dto/AbsenceSalarieDto";
import { EtatAbsencesEnum } from "../../../dto/GenApi.iNotPaie.Dto/EtatAbsencesEnum";
import { JourFerieDto } from "../../../dto/GenApi.iNotPaie.Dto/JourFerieDto";
import { TypeAbsenceSalarieDto } from "../../../dto/GenApi.iNotPaie.Dto/TypeAbsenceSalarieDto";
import { TypeCongeCompteurEnum } from "../../../dto/GenApi.iNotPaie.Dto/TypeCongeCompteurEnum";
import { CongeCalculationParams, CongeCalculationService } from "../../../services/CongeCalculationService";
import { MultiSelectOption } from "../../../shared/components/dropDownSelect/dropDownSelect";
import { NotificationServiceInstance } from "../../../shared/services/notification.service";
import { EventBus } from "../../Controls/EventBus";
import EventConstants from "../../Controls/EventConstants";
import { ParametresInstance } from "../../Controls/Parameters";
import { ServicesFactoryInstance } from "../../Controls/ServicesFactory";
import { CalendarDisabledDaysHelper } from "../../../Helper/calendarDisabledDaysHelper";
import { plageHoraire } from "../../../Helper/absencesHelper";
import { IAbsenceComplete } from "../../../shared/interfaces/absences.interface";

@Component({
    components: {
        CarteCongeLight: require("../carteCongeLight/carteCongeLight.vue.html")
    },
    computed: {
        ...mapState("AbsencesStore", ["storeTypesAbsencePosableSalarie"]),
        ...mapState("ParametersStore", ["storeVariableListeJoursFeries"])
    }
})
export default class ModalAjoutConge extends Vue {
    @Prop({ required: true, default: undefined })
    public exercice: Date | undefined;

    @Prop({ required: true, default: null })
    public absenceToModifyId!: number | null;

    public plageHoraire = plageHoraire;

    public absenceToModify: AbsenceSalarieDto | null = null;

    public listAbsencesLite: AbsenceLiteSalarieV2Dto[] = [];
    public dateDebutFilter = moment().subtract(3, "month").startOf("month").utcOffset(0, true).toDate();
    public dateFinFilter = moment().add(12, "months").endOf("month").utcOffset(0, true).toDate();

    public typeConges = TypeCongeCompteurEnum; // Types de congés
    public storeTypesAbsencePosableSalarie!: TypeAbsenceSalarieDto[]; // Mappé depuis le store Vuex
    public storeVariableListeJoursFeries!: JourFerieDto[]; // Mappé depuis le store Vuex

    public visible = false; // Visibilité du modal
    public dataIsReady = false; // Données prêtes

    public totalHeures: number | undefined = undefined;
    public totalHeuresError = "";

    /**
     * Options du calendrier pour mettre en évidence les jours fériés et désactiver certaines dates
     */
    public get pickerOptions(): any {
        return {
            cellClassName: this.cellClassName,
            firstDayOfWeek: 1, // Commence la semaine le lundi
            onPick: this.onDateSelected
        };
    }

    /**
     * Met à jour les inputs DOM du date-range picker
     * @param {minDate, maxDate} minDate: Date; maxDate: Date | null
     * @returns {void}
     */
    private onDateSelected({ minDate, maxDate }: { minDate: Date; maxDate: Date | null }): void {
        const rangeElement = this.$refs.rangeDatePicker as any;
        // Vérifie si le rangeDatePicker existe et si son élément DOM est disponible
        if (!rangeElement || !rangeElement.$el) {
            return;
        }

        // Récupère les deux inputs du date-range picker
        const [input1, input2] = Array.from(rangeElement.$el.querySelectorAll(".el-range-input") as NodeListOf<HTMLInputElement>);

        // Vérifie si le premier input est vide
        if (!input1.value) {
            // Aucune date sélectionnée : remplir les deux champs
            input1.value = minDate?.toLocaleDateString() ?? "";
            input2.value = maxDate?.toLocaleDateString() ?? "";
        } else if (input2.value) {
            // Plage complète déjà sélectionnée → reset pour une nouvelle sélection
            input1.value = minDate?.toLocaleDateString() ?? "";
            input2.value = "";
        } else if (maxDate && moment(maxDate).isBefore(minDate)) {
            // Sélection en ordre inversé (date fin avant date début)
            input1.value = maxDate.toLocaleDateString();
            input2.value = "";
        }
    }

    public get modalTitle(): string {
        return this.absenceToModifyId != null ? "MODIFICATION ABSENCE" : "DEMANDE D'ABSENCE";
    }

    /**
     * Propriétés pour la validation du formulaire
     * @returns {object}
     */
    public form = {
        selectedTypeConge: null as string | null,
        selectedDatesConge: null as Date[] | null,
        plageDateDebut: plageHoraire.matin as string,
        plageDateFin: plageHoraire.apresMidi as string,
        totalHeures: this.totalHeures as number | undefined
    };

    /**
     * Règles de validation du formulaire
     * @returns {object}
     */
    public rules = {
        selectedTypeConge: [
            { required: true, message: "Veuillez sélectionner un type d'absence", trigger: "change" }
        ],
        selectedDatesConge: [
            { required: true, message: "Veuillez sélectionner une période d'absence", trigger: ["blur", "change"] }
        ],
        plageDateDebut: [
            { required: true, message: "Veuillez sélectionner une plage horaire de début", trigger: "change" }
        ],
        plageDateFin: [
            { required: true, message: "Veuillez sélectionner une plage horaire de fin", trigger: "change" }
        ]
    };

    // Vérification manuelle de totalHeures
    // Erreur affichée uniquement à la soumission pour totalHeures (hors rules pour éviter trigger au clavier)
    @Watch("form.totalHeures")
    public setAbsence(newValue: number): void {
        if (newValue == null) {
            this.totalHeuresError = "Veuillez saisir le nombre d'heures";
        } else {
            this.totalHeuresError = "";
        }
    }

    public async mounted(): Promise<void> {
        this.visible = true;
        this.dataIsReady = false;

        try {
            await this.$store.dispatch("AbsencesStore/initializeTypesAbsences");
            await this.$store.dispatch("ParametersStore/initializeJoursFeries");

            await this.setAbsences();

            if (this.absenceToModifyId != null) {
                await this.setModalToModify();
            }
        } catch {
            this.hide();
        } finally {
            this.dataIsReady = true;
        }
    }

    /**
     * Méthode pour récupérer les absences de l'utilisateur connecté
     * @returns {Promise<void>}
     */
    private async setAbsences(): Promise<void> {
        try {
            this.listAbsencesLite = await ServicesFactoryInstance.AbsencesService.getAbsencesLiteSalarieConnecteV2(this.dateDebutFilter, this.dateFinFilter);
        } catch {
            NotificationServiceInstance.SendFailed("Une erreur est survenue lors de la récupération des absences.");
        }
    }

    /**
     * Getter pour obtenir la liste des absences complètes formatées
     * @returns Liste des absences complètes avec dateDebut, dateFin, plageDebut, plageFin
     */
    public get listAbsencesCompletes(): IAbsenceComplete[] {
        return this.listAbsencesLite
            .map(a => ({
                dateDebut: a.DateDebut,
                dateFin: a.DateFin,
                plageDebut: a.DebutMatinAprem!,
                plageFin: a.FinMatinAprem!
            }));
    }

    /**
     * Méthode pour initialiser le modal avec les données de l'absence à modifier
     * @returns {void}
     */
    public async setModalToModify(): Promise<void> {
        if (this.absenceToModifyId != null) {
            this.absenceToModify = await ServicesFactoryInstance.AbsencesService.getAbsenceAsync(this.absenceToModifyId);
            this.form.selectedTypeConge = this.absenceToModify.CodeTypeAbsence!;
            this.form.selectedDatesConge = [this.absenceToModify.DateDebut, this.absenceToModify.DateFin];
            this.form.plageDateDebut = this.absenceToModify.DebutMatinAprem!;
            this.form.plageDateFin = this.absenceToModify.FinMatinAprem!;
            this.form.totalHeures = this.absenceToModify.NombreHeures ?? 0;
            this.listAbsencesLite = this.listAbsencesLite.filter((absenceLite) => absenceLite.Id !== this.absenceToModify!.Id);
        }
    }

    /**
     * Méthode pour récupérer les types d'absences disponibles
     * @returns {MultiSelectOption[]}
     */
    public get setTypesAbsenceFilter(): MultiSelectOption[] {
        return this.storeTypesAbsencePosableSalarie.map((typeAbsence) => ({
            value: typeAbsence.Type as string,
            label: typeAbsence.Libelle as string
        }));
    }

    /**
     * Méthode pour masquer le modal
     * @returns {void}
     */
    public hide(): void {
        this.$emit("closeModal");
    }

    public completeValidation(): boolean {
        // Validation du nombre de jours
        if (this.isPeriodeJours && this.totalJours === 0) {
            NotificationServiceInstance.SendFailed("Le nombre de jours doit être supérieur à 0");

            return false;
        }

        // Validation du nombre d'heures
        if (!this.isPeriodeJours && (this.form.totalHeures === 0 || this.form.totalHeures === undefined)) {
            NotificationServiceInstance.SendFailed("Le nombre d'heures doit être supérieur à 0");

            return false;
        }

        return true;
    }

    /**
    * Méthode pour valider et soumettre le formulaire
    * @returns {Promise<void>}
    */
    public async validateAndSubmit(): Promise<void> {
        this.totalHeuresError = "";
        (this.$refs.form as any).validate((valid: boolean) => {
            // totalHeures n'est pas dans rules : on valide à la main pour n'afficher l'erreur qu'ici
            const totalHeuresInvalid = !this.isPeriodeJours && (this.form.totalHeures === undefined || this.form.totalHeures === null || this.form.totalHeures === 0);
            if (totalHeuresInvalid) {
                this.totalHeuresError = "Veuillez saisir le nombre d'heures";
            }
            if (valid && !totalHeuresInvalid && this.completeValidation()) {
                ServicesFactoryInstance.AbsencesService.enregistrerAbsence(
                    this.mapAbsenceToDto(),
                    true, // isActionSalarie
                    false, // envoyerEMailSalarie
                    () => {
                        NotificationServiceInstance.SendSuccess("Absence enregistrée avec succès");
                        EventBus.$emit(EventConstants.UPDATE_CONGES);
                        this.hide();
                    }
                );
            }
        });
    }

    /**
     * Vérifie si les radios sont désactivés
     * @returns {boolean}
     */
    public get isDisabledRadio(): boolean {
        if (this.form.selectedDatesConge != null) {
            if (moment(this.form.selectedDatesConge[0]).isSame(moment(this.form.selectedDatesConge[1]))) {
                if (this.form.plageDateDebut == plageHoraire.apresMidi) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Vérifie si la plage horaire matin est désactivée pour la date de début
     * @returns {boolean}
     */
    public get isDisabledDebutMatin(): boolean {
        if (this.form.selectedDatesConge != null) {
            return CalendarDisabledDaysHelper.isDateWithPlageDisabled(this.form.selectedDatesConge[0], plageHoraire.matin, this.relevantAbsences);
        }

        return false;
    }

    /**
     * Vérifie si la plage horaire après-midi est désactivée pour la date de début
     * @returns {boolean}
     */
    public get isDisabledDebutAprem(): boolean {
        if (this.form.selectedDatesConge != null) {
            return CalendarDisabledDaysHelper.isDateWithPlageDisabled(this.form.selectedDatesConge[0], plageHoraire.apresMidi, this.relevantAbsences);
        }

        return false;
    }

    /**
     * Vérifie si la plage horaire matin est désactivée pour la date de fin
     * @returns {boolean}
     */
    public get isDisabledFinMatin(): boolean {
        if (this.form.selectedDatesConge != null) {
            return CalendarDisabledDaysHelper.isDateWithPlageDisabled(this.form.selectedDatesConge[1], plageHoraire.matin, this.relevantAbsences);
        }

        return false;
    }

    /**
     * Vérifie si la plage horaire après-midi est désactivée pour la date de fin
     * @returns {boolean}
     */
    public get isDisabledFinAprem(): boolean {
        if (this.form.selectedDatesConge != null) {
            return CalendarDisabledDaysHelper.isDateWithPlageDisabled(this.form.selectedDatesConge[1], plageHoraire.apresMidi, this.relevantAbsences);
        }

        return false;
    }

    /**
     * Affiche le nombre de jours ou d'heures selon le type de congés sélectionné
     * @returns {boolean}
     */
    public get isPeriodeJours(): boolean {
        return this.form.selectedTypeConge !== null ? CongeCalculationService.estCongeCalculHeures(this.form.selectedTypeConge!) : true;
    }

    /**
     * Extrait le paramètre compteurIndexe depuis le libellé court du type d'absence
     * Format du libellé court: items[0]/items[1]/items[2]/items[3]/items[4] où items[4] est compteurIndexe
     * @returns {boolean} True si les jours non travaillés (week-ends et jours fériés) doivent être comptés, False sinon
     */
    private getCompterJoursNonTravailles(): boolean {
        if (!this.form.selectedTypeConge) {
            return false;
        }

        // N'a pas de libellé court -> false
        const typeAbsence = this.storeTypesAbsencePosableSalarie.find(t => t.Type === this.form.selectedTypeConge);
        if (!typeAbsence || !typeAbsence.LibelleCourt) {
            return false;
        }

        // Si compteurIndexe est définis à "0", on compte les jours non travaillés
        const items = typeAbsence.LibelleCourt.split("/");
        if (items.length === 6) {
            const compteurIndexe = items[5];
            return compteurIndexe === "0";
        }

        return false;
    }

    /**
     * Calcule le nombre de jours de congé en utilisant le service dédié
     * @returns {number} Nombre de jours de congé calculé
     */
    public get totalJours(): number {
        // Vérification des données requises
        if (!this.form.selectedDatesConge ||
            this.form.selectedDatesConge.length !== 2 ||
            !this.form.plageDateDebut ||
            !this.form.plageDateFin ||
            !this.storeVariableListeJoursFeries) {
            return 0;
        }

        const params: CongeCalculationParams = {
            dateDebut: this.form.selectedDatesConge[0],
            dateFin: this.form.selectedDatesConge[1],
            plageDateDebut: this.form.plageDateDebut,
            plageDateFin: this.form.plageDateFin,
            listeJoursFeries: this.storeVariableListeJoursFeries,
            compterJoursNonTravailles: this.getCompterJoursNonTravailles()
        };

        return CongeCalculationService.calculerNombreJours(params);
    }

    /**
     * Applique une classe CSS personnalisée aux cellules du calendrier
     * @param time - Timestamp de la date
     * @returns Classe CSS à appliquer
     */
    public cellClassName(time: number): string {
        return CalendarDisabledDaysHelper.cellClassName(time, this.listAbsencesCompletes, this.storeVariableListeJoursFeries);
    }

    public get relevantAbsences(): IAbsenceComplete[] {
        if (!this.form.selectedDatesConge) {
            return [];
        }
        const [dateDebut, dateFin] = this.form.selectedDatesConge;

        // Filtrer les absences qui chevauchent la période sélectionnée
        return CalendarDisabledDaysHelper.checkChevauchementDates(
            dateDebut,
            dateFin,
            this.listAbsencesCompletes
        );
    }

    /**
     * Vérifie si la date de fin respecte la règle : date fin ≤ exercice + 1 an
     * Règle alignée sur le backend (AbsencesService.cs)
     * @returns {boolean} True si la date de fin est valide
     */
    private checkDateFinValide(): boolean {
        if (!this.form.selectedDatesConge || this.form.selectedDatesConge.length !== 2) {
            return true; // N'a pas deux dates de selectionné
        }

        const dateFin = this.form.selectedDatesConge[1];

        if (!CongeCalculationService.estDateFinValide(dateFin)) {
            NotificationServiceInstance.SendFailed(
                `Une demande d'absence ne peut être effectuée que dans l'année à venir. Cette demande ne sera pas prise en compte.`
            );
            this.form.selectedDatesConge = null;

            return false;
        }

        return true;
    }

    /**
     * Vérifie si les plages horaires sélectionnées chevauchent une absence existante
     * @returns {void}
     */
    public checkPlageHoraire(): void {
        if (!this.form.selectedDatesConge) {
            return;
        }

        // Vérifie si la date de fin est >= à la fin de l'exercice + 1 an
        if (!this.checkDateFinValide()) {
            return;
        }

        // Si aucune absence ne chevauche la période sélectionnée, on peut sortir directement
        if (this.relevantAbsences.length === 0) {
            return;
        }

        // Essayer d'ajuster automatiquement les plages
        const adjustedPlages = CalendarDisabledDaysHelper.adjustPlageHoraires(
            this.form.selectedDatesConge,
            this.form.plageDateDebut,
            this.form.plageDateFin,
            this.relevantAbsences,
            plageHoraire
        );

        // Appliquer les plages ajustées au formulaire
        this.form.plageDateDebut = adjustedPlages.plageDateDebut;
        this.form.plageDateFin = adjustedPlages.plageDateFin;

        // Vérifier si après ajustement il y a toujours un conflit
        const hasConflict = CalendarDisabledDaysHelper.checkDateRangeHasExistingAbsence(
            this.form.selectedDatesConge[0],
            this.form.selectedDatesConge[1],
            this.form.plageDateDebut,
            this.form.plageDateFin,
            this.relevantAbsences
        );

        if (hasConflict) {
            const formatDateDebut = moment(this.form.selectedDatesConge[0]).format("DD/MM/YYYY");
            const formatDateFin = moment(this.form.selectedDatesConge[1]).format("DD/MM/YYYY");
            NotificationServiceInstance.SendFailed(`La période du ${formatDateDebut} au ${formatDateFin} contient des dates non disponibles`);
            this.form.selectedDatesConge = null;
        }
    }

    /**
     * Crée un objet DTO à partir de notre formulaire
     * @returns
     */
    public mapAbsenceToDto(): AbsenceSalarieDto {
        return {
            Id: this.absenceToModify ? this.absenceToModify.Id : 0,
            IdSalarie: this.absenceToModify ? this.absenceToModify!.IdSalarie : ParametresInstance.salarieConnecte?.IdSalarie ?? 0,
            IdContrat: this.absenceToModify ? this.absenceToModify!.IdContrat : ParametresInstance.salarieConnecte?.IdContrat || 0,
            DateDebut: moment(this.form.selectedDatesConge![0]).utcOffset(0, true).toDate(),
            DateFin: moment(this.form.selectedDatesConge![1]).utcOffset(0, true).toDate(),
            DebutMatinAprem: this.form.plageDateDebut,
            FinMatinAprem: this.form.plageDateFin,
            LibelleBulletin: this.absenceToModify ? this.absenceToModify!.LibelleBulletin : undefined,
            CodeTypeAbsence: this.form.selectedTypeConge!,
            Couleur: this.absenceToModify ? this.absenceToModify!.Couleur : undefined,
            NombreHeures: this.isPeriodeJours ? undefined : this.form.totalHeures,
            NombreJours: this.isPeriodeJours ? this.totalJours : undefined,
            NombreMois: this.absenceToModify ? this.absenceToModify!.NombreMois : undefined,
            NombreJoursCarence: this.absenceToModify ? this.absenceToModify!.NombreJoursCarence : undefined,
            CodeMotifReprises: this.absenceToModify ? this.absenceToModify!.CodeMotifReprises : undefined,
            DateDernierTravaille: this.absenceToModify ? this.absenceToModify!.DateDernierTravaille : undefined,
            DateRepriseReelle: this.absenceToModify ? this.absenceToModify!.DateRepriseReelle : undefined,
            Subrogation: this.absenceToModify ? this.absenceToModify!.Subrogation : false,
            SubrogationDebut: this.absenceToModify ? this.absenceToModify!.SubrogationDebut : undefined,
            SubrogationFin: this.absenceToModify ? this.absenceToModify!.SubrogationFin : undefined,
            Etat: this.absenceToModify ? this.absenceToModify!.Etat : EtatAbsencesEnum.EnAttenteValidationResponsableNiveau_1,
            Imposee: this.absenceToModify ? this.absenceToModify!.Imposee : false,
            NomSalarie: this.absenceToModify ? this.absenceToModify!.NomSalarie : undefined,
            PrenomSalarie: this.absenceToModify ? this.absenceToModify!.PrenomSalarie : undefined,
            Initiales: this.absenceToModify ? this.absenceToModify!.Initiales : undefined,
            LibelleTypeAbsence: this.absenceToModify ? this.absenceToModify!.LibelleTypeAbsence : undefined,
            NumeroValidationConge: this.absenceToModify ? this.absenceToModify!.NumeroValidationConge : undefined,
            ValeurAbsence: this.absenceToModify ? this.absenceToModify!.ValeurAbsence : undefined,
            Photo: this.absenceToModify ? this.absenceToModify!.Photo : undefined,
            DateDemande: this.absenceToModify ? this.absenceToModify!.DateDemande : undefined,
            DateValidation: this.absenceToModify ? this.absenceToModify!.DateValidation : undefined,
            CommentaireCollaborateur: this.absenceToModify ? this.absenceToModify!.CommentaireCollaborateur : undefined,
            CommentaireManager: this.absenceToModify ? this.absenceToModify!.CommentaireManager : undefined,
            IdValideurManageur: this.absenceToModify ? this.absenceToModify!.IdValideurManageur : undefined,
            IdValideurNotaire: this.absenceToModify ? this.absenceToModify!.IdValideurNotaire : undefined
        };
    }
}



// WEBPACK FOOTER //
// ./src/components/gestionDesAbsencesV2/modalAjoutConge/modalAjoutConge.ts